import { useRecipientFrameDraft } from './useRecipientFrameDraft';
import { ReceivedBoxGiftFrame } from './ReceivedBoxGiftFrame';
import { AvatarFramePicker } from '@src/components/faces/AvatarFramePicker';
import { trackGiftEvent } from '@src/utils/giftAnalytics';
import { UserAccountEmailInput } from '@lub0v/santa-utils';
import { translateFieldError, validateFieldError } from '@src/validation';
import { isDotCom, supportEmail } from '@src/site';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { BoxGiftInfo, BoxGiftHistory } from '@lub0v/santa-utils';
import { Trans, useTranslation } from 'react-i18next';
import { Spinner } from '@src/components/Spinner/Spinner';
import { Hint } from '@src/components/Hint/Hint';
import { EmailInput } from '@src/components/EmailInput/EmailInput';
import { Checkbox } from '@src/components/Checkbox/Checkbox';
import { Button } from '@src/components/Button/Button';
import { closeDialog } from '@src/components/Dialog/Dialog';
import { showToast } from '@src/components';
import { ParticipantFace } from '@src/components/faces/ParticipantFace';
import { PremiumBadge } from '@src/components/PremiumBadge/PremiumBadge';
import { premiumTierName } from '@src/components/PremiumBadge/tierIdentity';
import { HeartIcon } from '@src/components/icons/HeartIcon';
import { EyeOffIcon } from '@src/components/icons/EyeOffIcon';
import { StarIcon } from '@src/components/icons/StarIcon';
import { GiftIcon } from '@src/components/icons/GiftIcon';
import { ThemeArtwork } from '@src/theme/Artwork';
import { loadBox } from '@src/state/boxSlice';
import { useAppDispatch, useAppSelector } from '@src/state/hooks';
import './BoxGiftDialog.css';

/** Gift receipt, checkout and thank-you screens use persisted backend state. */
export type BoxGiftScreen = 'gift' | 'thanks' | 'give' | 'thanked' | 'sent' | 'payment' | 'hub';

/** Counts from confirmed gift allocation; recipientCount is distinct users across all recipient boxes. */
export interface GiftDeliverySummary { boxCount: number; recipientCount: number | null; }

export function BoxGiftDialog({ gift, history, boxKey, onGiftUpdated, deliverySummary: suppliedSummary, initialScreen, onScreenChange, onRepliesSeen, onGiftInteraction, onDismiss }: { gift: BoxGiftInfo | null; history?: BoxGiftHistory | null; boxKey: string; onGiftUpdated: (gift: BoxGiftInfo) => void; deliverySummary?: GiftDeliverySummary; initialScreen?: BoxGiftScreen; onDismiss: { current: (() => Promise<void>) | null }; onScreenChange: (screen: BoxGiftScreen) => void; onRepliesSeen?: (ids: number[]) => void; onGiftInteraction?: (action: 'action' | 'continue') => void }) {
  const { t, i18n } = useTranslation();
  const dotCom = isDotCom();
  const navigateTo = useNavigate();
  const dispatch = useAppDispatch();
  const user = useAppSelector(state => state.session.user);
  const profile = user?.profiles?.find(item => item.id === user.id) ?? user?.profiles?.find(item => item.active);
  const [screen, setScreen] = useState<BoxGiftScreen>(initialScreen ?? (gift?.thankedAt ? 'thanked' : 'gift'));
  useEffect(() => {
    if (screen !== 'hub') return;
    const ids = history?.replies.filter(reply => !reply.thankYouSeenAt).map(reply => reply.id) ?? [];
    if (!ids.length) return;
    // Send only this rendered snapshot, so a reply arriving meanwhile stays unread.
    void (async () => {
      try {
        for (let offset = 0; offset < ids.length; offset += 1000) {
          const response = await fetch(`/api/box/${encodeURIComponent(boxKey)}/gift/replies/seen`, {
            method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ replyIds: ids.slice(offset, offset + 1000) }),
          });
          if (!response.ok) throw new Error('Could not mark replies seen');
          const result = await response.json();
          if (Array.isArray(result.replyIds)) onRepliesSeen?.(result.replyIds);
        }
      } catch { /* Keep the gift jumping; reopening the inbox retries. */ }
    })();
  }, [screen, history, boxKey]);
  const [thanksDraft, setThanksDraft] = useState('');
  const [giftDraft, setGiftDraft] = useState('');
  const replyKeys = useRef<{thanks?: string; give?: string}>({});
  const messageContext = (kind: 'thanks' | 'give', text: string) => !text.trim() ? { message_type: 'empty' as const } : replyKeys.current[kind] ? { message_type: 'predefined' as const, message_key: replyKeys.current[kind] } : { message_type: 'custom' as const };
  const message = screen === 'give' ? giftDraft : thanksDraft;
  const setMessage = screen === 'give' ? setGiftDraft : setThanksDraft;
  const [thanked, setThanked] = useState(Boolean(gift?.thankedAt));
  const [sendingThank, setSendingThank] = useState(false);
  const thankInFlight = useRef(false);
  const [thankYouMessage, setThankYouMessage] = useState(gift?.thankYouMessage ?? '');
  const [shareGifterIdentity, setShareGifterIdentity] = useState(false);
  const [shareIdentity, setShareIdentity] = useState(false);
  const [selectedOfferCode, setSelectedOfferCode] = useState<string | null>(null);
  const [deliverySummary, setDeliverySummary] = useState(suppliedSummary ?? history?.summary);
  const [offers, setOffers] = useState<{code:string;count:number;amountMinor:number;currency:string;available:boolean}[]>([]);
  const availableOffers = offers.filter(offer => offer.available);
  const selectedOffer = offers.find(offer => offer.code === selectedOfferCode)
    ?? availableOffers[Math.floor(availableOffers.length / 2)];
  const quantity = selectedOffer?.count ?? 0;
  const selectedPrice = selectedOffer
    ? i18n.language.startsWith('ru') && selectedOffer.currency.toUpperCase() === 'RUB'
      ? `${new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 2 }).format(selectedOffer.amountMinor / 100)}р`
      : new Intl.NumberFormat(i18n.language, { style: 'currency', currency: selectedOffer.currency, minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(selectedOffer.amountMinor / 100)
    : '';
  const [checkoutAvailable, setCheckoutAvailable] = useState(false);
  const [checkoutBusy, setCheckoutBusy] = useState(false);
  const [checkoutError, setCheckoutError] = useState('');
  const checkoutContent = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (checkoutError && (screen === 'give' || screen === 'payment')) {
      checkoutContent.current?.scrollTo({ top: 0, behavior: 'instant' });
    }
  }, [checkoutError, screen]);
  const [receiptEmail, setReceiptEmail] = useState(user?.email ?? '');
  const [receiptEmailBlurred, setReceiptEmailBlurred] = useState(false);
  const receiptEmailError = validateFieldError(new UserAccountEmailInput(), 'email', { email: receiptEmail.trim() });
  const [paymentId, setPaymentId] = useState(() => initialScreen === 'payment' ? Number(new URLSearchParams(window.location.search).get('payment')) || null : null);
  const [paymentNeedsHelp, setPaymentNeedsHelp] = useState(false);
  const [paymentReceived, setPaymentReceived] = useState(false);
  const [confirmedPaymentId, setConfirmedPaymentId] = useState<number | null>(history?.paymentId ?? null);
  const frameSaveInFlight = useRef<Promise<void> | null>(null);
  const request = useRef<{body:string;id:string} | null>(null);
  const checkoutInFlight = useRef(false);
  const [testPriceQuery] = useState(() => {
    const search = new URLSearchParams(window.location.search);
    const query = new URLSearchParams();
    for (const key of ['automaticPayment', 'price']) if (search.has(key)) query.set(key, search.get(key)!);
    return query.toString() ? `?${query}` : '';
  });
  useEffect(() => {
    if(screen!=='give')return;
    let active=true;
    void fetch(`/api/box/${encodeURIComponent(boxKey)}/gift/offers${testPriceQuery}`,{credentials:'same-origin'}).then(async r=>{if(!r.ok)throw Error();return r.json();}).then(data=>{if(active){setOffers(data.offers);setCheckoutAvailable(data.checkoutAvailable);}}).catch(()=>{if(active)setCheckoutError(t('boxGift.checkoutUnavailable'));});
    return ()=>{active=false;};
  },[screen,boxKey,t]);
  useEffect(()=>{
    if(!paymentId)return;
    let active=true, checking=false;
    let currentCheck: AbortController | null = null;
    setPaymentNeedsHelp(false);setPaymentReceived(false);
    const helpTimer=window.setTimeout(()=>setPaymentNeedsHelp(true),120000);
    async function check() {
      if(checking)return;checking=true;
      const controller = new AbortController(); currentCheck = controller;
      const timeout = window.setTimeout(() => controller.abort(), 15000);
      try {
        const response=await fetch(`/api/box/${encodeURIComponent(boxKey)}/gift/payment?payment=${paymentId}`,{credentials:'same-origin', signal: controller.signal});
        if(!response.ok)throw Error();
        const data=await response.json();if(!active)return;
        if(data.status==='fulfilled') {
          setDeliverySummary(data.summary);setConfirmedPaymentId(paymentId);setPaymentId(null);navigate('sent');
        } else if(data.status==='allocating') {
          setPaymentReceived(true);setPaymentNeedsHelp(true);
        } else if(['failed','cancelled'].includes(data.status)) {
          setCheckoutError(t('boxGift.checkoutFailed'));setPaymentId(null);request.current=null;navigate('give');
        }
      } catch {if(active)setCheckoutError(t('boxGift.paymentCheckError'));}
      finally {window.clearTimeout(timeout); checking=false; currentCheck=null;}
    }
    void check();const timer=window.setInterval(()=>void check(),5000);
    const onVisible = () => { if (document.visibilityState === 'visible') void check(); };
    window.addEventListener('focus',check);
    document.addEventListener('visibilitychange',onVisible);
    return ()=>{active=false;currentCheck?.abort();document.removeEventListener('visibilitychange',onVisible);window.clearTimeout(helpTimer);window.clearInterval(timer);window.removeEventListener('focus',check);};
  },[paymentId,boxKey,t]);
  async function checkout() {
    if(checkoutInFlight.current)return;
    const offer=selectedOffer;
    if (receiptEmailError) { setReceiptEmailBlurred(true); checkoutContent.current?.querySelector<HTMLInputElement>('input[name="receiptEmail"]')?.focus(); return; }
    if(!offer?.available || !checkoutAvailable) {setCheckoutError(t('boxGift.checkoutUnavailable'));return;}
    onGiftInteraction?.('action');
    trackGiftEvent('santa_gift_checkout_clicked', { screen: 'give', box_count: quantity, anonymous: !shareGifterIdentity, ...messageContext('give', giftDraft) });
    const tab=window.open('about:blank','_blank');
    if(!tab){setCheckoutError(t('boxGift.popupBlocked'));return;}
    tab.opener=null;
    // Populate synchronously while the tab is same-origin, before requesting checkout.
    tab.document.title = t('boxGift.preparingCheckout');
    const container = tab.document.createElement('main');
    container.style.cssText = 'max-width:480px;margin:20vh auto;padding:24px;font:18px system-ui;text-align:center;color:#213b63';
    const label = tab.document.createElement('p');
    label.textContent = t('boxGift.preparingCheckout');
    const progress = tab.document.createElement('span');
    progress.setAttribute('role', 'status');
    progress.dataset.testid = 'gift-checkout-spinner';
    progress.setAttribute('aria-label', t('boxGift.preparingCheckout'));
    progress.style.cssText = 'display:inline-block;width:32px;height:32px;border:3px solid currentColor;border-top-color:transparent;border-radius:50%;animation:gift-spin 1s linear infinite';
    const style = tab.document.createElement('style');
    style.textContent = '@keyframes gift-spin{to{transform:rotate(360deg)}} @media(prefers-reduced-motion:reduce){[data-testid=gift-checkout-spinner]{animation-duration:3s!important}}';
    tab.document.head.append(style);
    container.append(label, progress);
    tab.document.body.replaceChildren(container);
    checkoutInFlight.current=true;setCheckoutBusy(true);setCheckoutError('');
    const body=JSON.stringify({offerCode:offer.code,message:giftDraft.trim(),giftAnonymously:!shareGifterIdentity,parentGiftId:gift?.id ?? null,receiptEmail:receiptEmail.trim()});
    if(request.current?.body!==body)request.current={body,id:crypto.randomUUID()};
    try {
      const response=await fetch(`/api/box/${encodeURIComponent(boxKey)}/gift/checkout${testPriceQuery}`,{method:'POST',credentials:'same-origin',headers:{'Content-Type':'application/json'},body:JSON.stringify({...JSON.parse(body),requestId:request.current.id})});
      if(!response.ok) {
        const error = await response.json().catch(() => null);
        throw new Error(error?.error?.message === 'GIFT_RECIPIENTS_UNAVAILABLE' ? 'GIFT_RECIPIENTS_UNAVAILABLE' : 'CHECKOUT_FAILED');
      }
      const data=await response.json();
      setPaymentId(data.id);navigate('payment');tab.location.href=data.paymentUrl;
    } catch (error) {tab.close();setCheckoutError(t(error instanceof Error && error.message === 'GIFT_RECIPIENTS_UNAVAILABLE' ? 'boxGift.noRecipients' : 'boxGift.checkoutFailed'));}
    finally {checkoutInFlight.current=false;setCheckoutBusy(false);}
  }
  const recipientCard = useAppSelector(state => state.box.card ?? state.box.cards.find(card => card.isYou));
  const recipientFrame = useRecipientFrameDraft(boxKey, gift?.id, recipientCard?.frameName, frame => {
    trackGiftEvent('santa_gift_received_frame_chosen', { frame_type: frame });
  });
  const savedFrameChoice = useRef<string | null>(null);
  const [selectedFrame, setSelectedFrame] = useState(history?.frameName || 'frame_2');
  const chooseFrame = (name: string) => { setSelectedFrame(name); setUseWizardFrame(true); };
  const [useWizardFrame, setUseWizardFrame] = useState(history?.applyFrame ?? true);
  const showSenderFrameReward = history?.summary.boxCount !== 0 && !gift;
  useEffect(() => {
    onDismiss.current = gift ? recipientFrame.save : showSenderFrameReward && confirmedPaymentId ? async () => {
      const choiceKey = JSON.stringify([confirmedPaymentId, useWizardFrame ? selectedFrame : null]);
      const save = async () => {
        if (savedFrameChoice.current === choiceKey) return;
        const response = await fetch(`/api/box/${encodeURIComponent(boxKey)}/gift/frame`, {
          method: 'POST', credentials: 'same-origin', keepalive: true,
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ paymentId: confirmedPaymentId, applyFrame: useWizardFrame, frameName: useWizardFrame ? selectedFrame : null }),
        });
        if (!response.ok) throw new Error('Frame choice not saved');
        savedFrameChoice.current = choiceKey;
        await dispatch(loadBox({ key: boxKey }));
      };
      const pending = (frameSaveInFlight.current ?? Promise.resolve()).catch(() => {}).then(save);
      frameSaveInFlight.current = pending;
      try { await pending; } finally { if (frameSaveInFlight.current === pending) frameSaveInFlight.current = null; }
    } : null;
    return () => { onDismiss.current = null; };
  }, [gift, recipientFrame.save, confirmedPaymentId, boxKey, useWizardFrame, selectedFrame, showSenderFrameReward, onDismiss, dispatch]);
  const sentBoxCount = deliverySummary?.boxCount ?? quantity;
  const sentArtworkCount = Math.max(1, Math.min(sentBoxCount, 10));
  // Only confirmed allocation can supply the number of recipients.
  const sentRecipientCount = deliverySummary?.recipientCount ?? null;
  async function continueGame() {
    onGiftInteraction?.('continue');
    closeDialog();
    if (/\/gifted$/.test(window.location.pathname)) {
      const search = new URLSearchParams(window.location.search);
      search.delete('result');
      search.delete('payment');
      const query = search.toString();
      navigateTo(`/box/${encodeURIComponent(boxKey)}${query ? `?${query}` : ''}`, { replace: true });
    }
  }
  function navigate(next: BoxGiftScreen) {
    if (['gift', 'thanked', 'hub', 'sent'].includes(screen) && (next === 'thanks' || next === 'give')) {
      onGiftInteraction?.('action');
      trackGiftEvent(next === 'thanks' ? 'santa_gift_thank_clicked' : 'santa_gift_spread_clicked', { screen });
    }
    setScreen(next); onScreenChange(next);
  }
  async function sendThankYou() {
    if (thankInFlight.current) return;
    thankInFlight.current = true;
    setSendingThank(true);
    try {
      const response = await fetch(`/api/box/${encodeURIComponent(boxKey)}/gift/thank`, {
        method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ giftId: gift?.id, message: thanksDraft.trim(), thankAnonymously: !shareIdentity }),
      });
      if (!response.ok) throw new Error('Thank-you unavailable');
      const updated = await response.json() as BoxGiftInfo;
      if (updated.thankedRecorded) trackGiftEvent('santa_gift_thanked', { anonymous: updated.thankAnonymously === true, ...messageContext('thanks', thanksDraft) });
      onGiftUpdated(updated);
      setThankYouMessage(updated.thankYouMessage ?? '');
      setThanked(true);
      setThanksDraft('');
      navigate('thanked');
    } catch {
      showToast(t('boxGift.thankError'));
    } finally {
      thankInFlight.current = false;
      setSendingThank(false);
    }
  }
  if (screen === 'payment') return <div className="box-gift-dialog">
    <div className="box-gift-dialog__content" ref={checkoutContent}><Spinner size="medium" className="box-gift-dialog__spinner" label={t('boxGift.paymentPending')} testId="gift-payment-spinner" />{checkoutError && <Hint tone="error" testId="box-gift-payment-error">{checkoutError}</Hint>}{paymentNeedsHelp ? <Hint tone="error" testId="box-gift-payment-help">
      <p>{t(paymentReceived ? 'boxGift.deliveryDelayed' : 'boxGift.paymentDelayed')}</p>
      <p><a href={`mailto:${supportEmail()}?subject=${encodeURIComponent(`Gift payment ${paymentId}`)}&body=${encodeURIComponent(`Payment ID: ${paymentId}\nBox: ${window.location.origin}/box/${encodeURIComponent(boxKey)}\n`)}`}>{t('boxGift.contactSupport', { email: supportEmail() })}</a></p>
      <p>{t('boxGift.paymentReference', { id: paymentId })}</p>
    </Hint> : <p role="status">{t('boxGift.paymentPending')}</p>}</div>
    <div className="box-gift-dialog__actions"><Button fullWidth variant="secondary" onClick={()=>navigate('give')}>{t('boxGift.backToSelection')}</Button></div>
  </div>;
  if (screen === 'sent' || screen === 'hub') return (
    <div className="box-gift-dialog box-gift-dialog--celebration" data-testid="box-gift-sent">
      <div className="box-gift-dialog__content">
        <div className={`box-gift-dialog__package-art box-gift-dialog__package-art--${sentArtworkCount} box-gift-dialog__sent-gifts${sentArtworkCount > 5 ? ' box-gift-dialog__sent-gifts--many' : ''}`} data-testid="box-gift-sent-art" aria-hidden="true">
          {Array.from({ length: sentArtworkCount }, (_, index) => {
            // Three loose rows, centered independently, with larger gifts in front.
            const rows = sentArtworkCount <= 7 ? [3, sentArtworkCount - 3] : [3, 3, sentArtworkCount - 6];
            const row = index < rows[0] ? 0 : index < rows[0] + rows[1] ? 1 : 2;
            const column = index - (row === 0 ? 0 : row === 1 ? rows[0] : rows[0] + rows[1]);
            const crowded = rows[row] === 4;
            const width = crowded ? 27 : 31;
            const spacing = crowded ? 24 : 29;
            const left = (100 - (width + (rows[row] - 1) * spacing)) / 2 + column * spacing;
            const top = row * (rows.length === 2 ? 37 : 24) + [0, 5, 2, 7][column];
            if (sentArtworkCount <= 5) return <ThemeArtwork key={index} role="gift" className="box-gift-dialog__package-gift" />;
            return <span key={index} className="box-gift-dialog__cluster-item" style={{ left: `${left}%`, top: `${top}%`, width: `${width}%`, height: `${row === 0 ? 40 : 44}%`, transform: `rotate(${[-9, 7, -4, 10][(column + row) % 4]}deg)`, zIndex: row }}><ThemeArtwork role="gift" className="box-gift-dialog__package-gift" /></span>;
          })}
        </div>
        <p role="status" data-testid="box-gift-recipient-count">
          {history?.summary.boxCount === 0 ? t('game.ward.personalGiftSent') : <Trans
            i18nKey={sentRecipientCount === null ? 'boxGift.sentImpactPending' : 'boxGift.sentImpact'}
            values={{ boxes: t('boxGift.impactBoxes', { count: sentBoxCount }), participants: t('boxGift.impactParticipants', { count: sentRecipientCount ?? 0 }) }}
            components={{ strong: <strong /> }}
          />}
        </p>
        {screen === 'hub' && gift && <ReceivedBoxGiftFrame draft={recipientFrame} />}
        {showSenderFrameReward && <section className="box-gift-dialog__frame-reward">
          <div className="box-gift-dialog__sparkles" aria-hidden="true"><span>✦</span><span>✧</span><span>✦</span><span>✧</span><span>✦</span><span>✦</span></div>
          <AvatarFramePicker card={profile} index={user?.id} selectedFrame={selectedFrame} onChange={chooseFrame} />
          <p>{t('boxGift.frameDescription')}</p>
          <Checkbox fit checked={useWizardFrame} onCheckedChange={checked => { setUseWizardFrame(checked); }} label={t('boxGift.useFrame')} testId="box-gift-use-frame" />
        </section>}
        {screen === 'hub' && typeof history?.openedRecipientCount === 'number' && <p role="status" data-testid="box-gift-opened-count">{t('boxGift.openedCount', { count: history.openedRecipientCount })}</p>}
        {screen === 'hub' && <section className="box-gift-dialog__inbox" data-testid="box-gift-inbox">
          <h3>{t('boxGift.repliesTitle')} <span aria-hidden="true">♡</span></h3>
          {history?.replies.length ? <ul className="box-gift-dialog__reply-list">{history.replies.map(reply => <li key={reply.id}>
            <div className="box-gift-dialog__reply-sender">
              {reply.name ? <Trans i18nKey="boxGift.replyFrom" components={{ sender: <strong>{reply.name}</strong> }} /> : <strong>{t('boxGift.anonymousReply')}</strong>}
            </div>
            <p>{reply.message || t('boxGift.emptyReply')}</p>
            <time dateTime={reply.thankedAt}>{new Date(reply.thankedAt).toLocaleDateString(i18n.language)}</time>
          </li>)}</ul> : <div className="box-gift-dialog__empty-inbox"><p>{t('boxGift.noReplies')}</p><ThankYouLetters /></div>}
        </section>}
        <div className="box-gift-dialog__follow-up">
          <p><strong>{t('boxGift.sentThanks')}</strong> <span aria-hidden="true">✨</span></p>
          <p className="box-gift-dialog__hub-hint">{t('boxGift.giftHubHint')}</p>
        </div>
      </div>
      <div className="box-gift-dialog__actions">
        {screen === 'hub' && history?.summary.boxCount !== 0 && <Button fullWidth variant="secondary" icon={<GiftIcon />} onClick={async () => {
          navigate('give');
        }} testId="box-gift-spread">{t('boxGift.giveAgain')}</Button>}
        <Button fullWidth onClick={continueGame} testId="box-gift-finish">{t('boxGift.continuePlaying')}</Button>
      </div>
    </div>
  );
  if (screen === 'thanks' || screen === 'give') return (
    <div className={`box-gift-dialog${screen === 'give' ? ' box-gift-dialog--give' : ''}`}>
      <div className="box-gift-dialog__content" ref={checkoutContent}>
      {screen === 'thanks' && <div className="box-gift-dialog__receipt-hero" data-testid="box-gift-thanks-art">
        <span className="box-gift-dialog__receipt-sparkles" aria-hidden="true"><i>✦</i><i>✧</i><i>✦</i><i>✧</i><i>✦</i></span>
        <ThemeArtwork role="gift" className="box-gift-dialog__art" />
      </div>}
      {screen === 'give' && checkoutError && <Hint tone="error" testId="box-gift-payment-error">{checkoutError}</Hint>}
      {screen === 'give' && <>
        <div className="box-gift-dialog__packages" role="group" aria-label={t('boxGift.choosePackage')}>
          {offers.map(({code,count,amountMinor,currency,available}) => <button type="button" disabled={checkoutBusy || !available} key={code} className="box-gift-dialog__package" aria-pressed={selectedOffer?.code === code} onClick={() => setSelectedOfferCode(code)} data-testid={`box-gift-package-${count}`}>
            <span className="box-gift-dialog__package-check" aria-hidden="true">✓</span>
            <span className={`box-gift-dialog__package-art box-gift-dialog__package-art--${Math.min(count, 5)}`} aria-hidden="true">
              {Array.from({length: Math.min(count, 5)}, (_, index) => <ThemeArtwork key={index} role="gift" className="box-gift-dialog__package-gift" />)}
            </span>
            <span className="box-gift-dialog__package-count">{t('boxGift.boxCount', {count})}</span>
            <strong className="box-gift-dialog__package-price">{new Intl.NumberFormat(i18n.language,{style:'currency',currency,minimumFractionDigits:0,maximumFractionDigits:2}).format(amountMinor/100)}</strong>
          </button>)}
        </div>
        <div className="box-gift-dialog__benefits box-gift-dialog__giving-benefits">
          <p className="box-gift-dialog__status">{t('boxGift.receiversStatus')} <PremiumBadge tier="tier_1">{premiumTierName('tier_1', i18n.language.startsWith('ru'))}</PremiumBadge></p>
          <p>{t('boxGift.givingBenefits', { context: dotCom ? 'com' : undefined })}</p>
        </div>
      </>}
      {<div className="box-gift-dialog__quick-replies">
        <p className="box-gift-dialog__section-label">{t(screen === 'thanks' ? 'boxGift.quickReply' : 'boxGift.messageLabel')}</p>
        <div className="box-gift-dialog__reply-grid">
          {['surprise', 'magic', 'heart', 'kindness'].map((reply) => {
            const text = t(`boxGift.${screen === 'thanks' ? 'replies' : 'givingReplies'}.${reply}`);
            const separator = text.indexOf(' ');
            const emoji = text.slice(0, separator);
            const label = text.slice(separator + 1);
            return <Button key={reply} variant={message === text ? 'soft' : 'secondary'} size="small" aria-pressed={message === text} onClick={() => { replyKeys.current[screen === 'thanks' ? 'thanks' : 'give'] = reply; setMessage(text); }} testId={`box-gift-reply-${reply}`}><span className="box-gift-dialog__reply-emoji" aria-hidden="true">{emoji}</span><span>{label}</span></Button>;
          })}
        </div>
      </div>}
      <label className="box-gift-dialog__field">
        {t('boxGift.customMessageLabel')}
        <textarea value={message} onChange={event => { replyKeys.current[screen === 'thanks' ? 'thanks' : 'give'] = undefined; setMessage(event.target.value); }} maxLength={300} rows={2} placeholder={t(screen === 'thanks' ? 'boxGift.thankPlaceholder' : 'boxGift.givePlaceholder')} />
        {screen === 'give' && <span className="box-gift-dialog__character-count">{message.length}/300</span>}
      </label>
      <Checkbox
        className="box-gift-dialog__privacy"
        checked={screen === 'thanks' ? shareIdentity : shareGifterIdentity}
        onCheckedChange={screen === 'thanks' ? setShareIdentity : setShareGifterIdentity}
        label={t(screen === 'thanks' ? 'boxGift.shareIdentity' : 'boxGift.shareGifterIdentity')}
        hint={t(screen === 'thanks' ? 'boxGift.shareIdentityHint' : 'boxGift.shareGifterIdentityHint')}
        testId="box-gift-share-identity"
      />
      {screen==='give' && <>
        <EmailInput
          name="receiptEmail"
          label={t('boxGift.receiptEmail')}
          value={receiptEmail}
          onValueChange={setReceiptEmail}
          onBlur={() => setReceiptEmailBlurred(true)}
          error={receiptEmailBlurred && receiptEmailError ? translateFieldError(receiptEmailError, t) : undefined}
          testId="box-gift-receipt-email"
          required
          maxLength={64}
        />
        {(!checkoutAvailable || (offers.length > 0 && !selectedOffer?.available)) && <p role="status">{t('boxGift.checkoutUnavailable')}</p>}
      </>}
      </div>
      <div className="box-gift-dialog__actions">
      <Button fullWidth icon={screen === 'give' ? <GiftIcon /> : undefined} loading={sendingThank || checkoutBusy} disabled={screen==='give' && (!!receiptEmailError || !checkoutAvailable || !selectedOffer?.available)} onClick={screen === 'thanks' ? () => void sendThankYou() : () => void checkout()} testId="box-gift-send-thank">{screen === 'thanks' ? t('boxGift.sendThank') : selectedOffer ? t('boxGift.sendJoyWithPrice', { count: quantity, price: selectedPrice }) : t('boxGift.sendJoy')}</Button>
      <Button fullWidth variant="ghost" onClick={() => navigate(history ? 'hub' : thanked ? 'thanked' : 'gift')}>{t('boxGift.back')}</Button>
      </div>
    </div>
  );
  return (
    <div className={`box-gift-dialog${thanked ? ' box-gift-dialog--thanked' : ' box-gift-dialog--received'}`}>
      <div className="box-gift-dialog__content">
      {!thanked && gift?.gifter && <div className="box-gift-dialog__sender-avatar" data-testid="box-gift-sender-avatar">
        <ParticipantFace card={gift.gifter} picture={gift.gifter.picture || 'unknown'} />
      </div>}
      {thanked ? <p role="status">{t('boxGift.thankConfirmation')}</p> : <div className="box-gift-dialog__sender">
        <p>{gift?.gifter ? <Trans i18nKey={gift.message ? "boxGift.namedSenderWithMessage" : "boxGift.namedSender"} components={{ sender: <strong>{gift.gifter.name}</strong> }} /> : t(gift?.message ? 'boxGift.anonymousSenderWithMessage' : 'boxGift.anonymousSender')}</p>
      </div>}
      {thanked && thankYouMessage && <blockquote data-testid="box-gift-thank-message">{thankYouMessage}</blockquote>}
      {!thanked && gift?.message && <blockquote>{gift?.message}</blockquote>}
      {gift && <ReceivedBoxGiftFrame draft={recipientFrame} />}
      {!thanked && <div className="box-gift-dialog__benefits">
        <p className="box-gift-dialog__status">{t('boxGift.status')} <PremiumBadge tier="tier_1">{premiumTierName('tier_1', i18n.language.startsWith('ru'))}</PremiumBadge></p>
        <ul className="box-gift-dialog__benefit-list">
          {(dotCom ? ['personalThemes', 'boxThemes', 'frames'] : ['adFree', 'personalThemes', 'boxThemes', 'frames']).map(benefit => <li key={benefit}>
            <span className="box-gift-dialog__benefit-icon" aria-hidden="true">{benefit === 'adFree' ? <EyeOffIcon /> : <StarIcon />}</span>
            <span>{t(`boxGift.receivedBenefits.${benefit}`)}</span>
          </li>)}
        </ul>
        <p className="box-gift-dialog__benefit-expiry">{t('boxGift.season')}</p>
      </div>}
      {thanked && <div className="box-gift-dialog__benefits box-gift-dialog__share-intro">
        <p><strong>{t('boxGift.shareMagic')}</strong></p>
        <p>{t('boxGift.shareBoxes')}</p>
        <p className="box-gift-dialog__status">{t('boxGift.shareStatus')} <PremiumBadge tier="tier_1">{premiumTierName('tier_1', i18n.language.startsWith('ru'))}</PremiumBadge></p>
        <p>{t('boxGift.shareBenefits', { context: dotCom ? 'com' : undefined })}</p>
      </div>}
      </div>
      <div className="box-gift-dialog__actions">
        {!thanked && <Button fullWidth variant="primary" icon={<HeartIcon />} onClick={() => navigate('thanks')} testId="box-gift-thank">{t('boxGift.thank')}</Button>}
        <Button fullWidth variant={thanked ? 'primary' : 'secondary'} icon={<GiftIcon />} onClick={() => navigate('give')} testId="box-gift-spread">{t('boxGift.spread')}</Button>
        <Button fullWidth variant={thanked ? 'secondary' : 'ghost'} size={thanked ? undefined : 'small'} onClick={continueGame}>{t(thanked ? 'boxGift.continuePlaying' : 'boxGift.enjoy')}</Button>
      </div>
    </div>
  );
}

/** Decorative stationery for the empty thank-you inbox; no sender data. */
function ThankYouLetters() {
  return <svg className="box-gift-dialog__letters" viewBox="0 0 130 100" fill="none" aria-hidden="true">
    <g transform="rotate(-12 48 58)"><rect x="7" y="34" width="78" height="49" rx="9" fill="#E8DDED" /><rect x="12" y="29" width="69" height="47" rx="7" fill="#FFF9EE" /><path d="m15 34 31 24 31-24" stroke="#E7CBA5" strokeWidth="2" /></g>
    <g transform="rotate(10 82 40)"><rect x="42" y="13" width="79" height="52" rx="9" fill="#E8DDED" /><rect x="48" y="8" width="70" height="48" rx="7" fill="#FFF9EE" /><path d="M70 21c-6-8-17 2 0 13 17-11 6-21 0-13Z" fill="#FF7777" /><path d="M87 23h23M87 29h19M58 42h49" stroke="#DECDB4" strokeWidth="3" strokeLinecap="round" /></g>
    <path d="m20 10 2 6 6 2-6 2-2 6-2-6-6-2 6-2Zm93 65 2 6 6 2-6 2-2 6-2-6-6-2 6-2Z" fill="#F4B43E" />
  </svg>;
}
